import { Component, OnInit, Inject, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray, AbstractControl } from '@angular/forms';
import { generateAddressFG } from 'src/app/models/address.model';
import { SelectOption } from '../../models/select-option.model';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PersonType } from '../../models/person-type.model';
import { Tenant } from '../../models/tenant.model';
import { Owner } from '../../models/owner.model';
import { ErrorResponse } from '../../models/error.model';
import { Warranty } from '../../models/warranty.model';
import { WarrantyContract, ContractType } from 'src/app/models/contract.model';
import { generatePhysicalPersonFG } from 'src/app/models/physical-person.model';
import { generateLegalPersonFG } from 'src/app/models/legal-person.model';
import { UtilsService } from '../../services/utils.service';
import { GuarantiesService } from '../../services/guaranties.service';
import { ServiciosContratadosService } from '../../services/servicios-contratados.service';
import { Province } from 'src/app/models/locality.model';
import { ServicioContratado } from '../../models/servicio-contratado.model';
import * as moment from 'moment';

@Component({
    selector: 'app-generate-contract-dialog',
    templateUrl: './generate-contract-dialog.component.html',
    styleUrls: ['./generate-contract-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GenerateContractDialogComponent implements OnInit {


    signatories: any[] = [];
    serviciosContratados: ServicioContratado[] = [];

    updatePeriods: SelectOption[] = [
        { codigo: 3, descripcion: 'Trimestral' },
        { codigo: 6, descripcion: 'Semestral' },
        { codigo: 12, descripcion: 'Anual' },
    ];

    showSection: any = {
        general: true,
        propiedad: false,
        alquiler: false,
        pago: false,
        servicios: false, // ← NUEVA SECCIÓN
    };

    contractFG = new UntypedFormGroup({
        fechaInicioAlquiler: new UntypedFormControl('', [Validators.required]),
        fechaFinAlquiler: new UntypedFormControl('', [Validators.required]),
        fechaFirma: new UntypedFormControl('', [Validators.required]),
        firmaPresencial: new UntypedFormControl(true, [Validators.required]),
        ciudadFirma: new UntypedFormControl('', [Validators.required]), // Cuando se agregue la posibilidad de firma digital hay que modificar el validator para que no aplique cuando está seleccionado
        direccionPropiedad: generateAddressFG(),
        propietarios: new UntypedFormArray([], []),
        alquiler: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
        expensas: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
        actualizacion: new UntypedFormControl({ value: '', disabled: true }, []),
        periodoActualizacion: new UntypedFormControl({ value: '', disabled: true }, []),
        inquilinos: new UntypedFormArray([], []),
        opcionPago: new UntypedFormGroup({
            cuotas: new UntypedFormControl('', [Validators.required]),
            montoPorCuota: new UntypedFormControl('', [Validators.required]),
            montoTotal: new UntypedFormControl('', [Validators.required]),
            tipo: new UntypedFormControl('', []),
            codigo: new UntypedFormControl('', []),
        }),
        // =============== NUEVOS CAMPOS ===============
        topeAguaYCloacas: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
        topeElectricidad: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
        topeGas: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
        topeABL: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
        expensasOrdinariasTope: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
        firmante: new UntypedFormControl(null, Validators.required)
    });


    get direccionFG() {
        return this.contractFG.controls.direccionPropiedad as UntypedFormGroup;
    }

    get propietariosArray() {
        return this.contractFG.controls.propietarios as UntypedFormArray;
    }

    get propietariosArrayControls() {
        return this.propietariosArray.controls as UntypedFormGroup[];
    }

    get inquilinosArray() {
        return this.contractFG.controls.inquilinos as UntypedFormArray;
    }

    get inquilinosArrayControls() {
        return this.inquilinosArray.controls as UntypedFormGroup[];
    }

    get opcionPagoFG() {
        return this.contractFG.controls.opcionPago as UntypedFormGroup;
    }

    constructor(
        private dialogRef: MatDialogRef<GenerateContractDialogComponent>,
        private guarantiesService: GuarantiesService,
        private serviciosContratadosService: ServiciosContratadosService,
        public utilsService: UtilsService,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: { warranty: Warranty, provinces?: Province[] }
    ) { }

    ngOnInit(): void {
        this.signatories = this.guarantiesService.getSignatories();

        const tipoProducto = this.data.warranty.cotizacion?.tipo as any;
        if (tipoProducto) {
            this.serviciosContratadosService.getServiciosContratados(0, 100, [], tipoProducto).subscribe({
                next: response => { this.serviciosContratados = response.body ?? []; }
            });
        }

        if (this.data.warranty.contratos) {
            this.updateFormGroupFromContract(this.data.warranty.contratos);
        } else {
            this.updateFormGroupFromWarranty(this.data.warranty);
        }
        this.contractFG.controls.fechaInicioAlquiler.valueChanges.subscribe(newValue => {
            if (newValue) {
                this.contractFG.controls.fechaFinAlquiler.setValue(moment(newValue).add(3, 'years').add(-1, 'days').toDate());
            }
        });
        this.cdr.detectChanges();
    }

    cerrarDialogo(response?: Warranty) {
        this.dialogRef.close(response);
    }

    updateFormGroupFromContract(contract: WarrantyContract) {
        // Variables sueltas
        this.contractFG.controls.fechaInicioAlquiler.setValue(contract.datos.fechaInicioAlquiler);
        this.contractFG.controls.fechaFinAlquiler.setValue(contract.datos.fechaFinAlquiler);
        this.contractFG.controls.fechaFirma.setValue(contract.datos.fechaFirma);
        this.contractFG.controls.firmaPresencial.setValue(contract.firmaPresencial);
        this.contractFG.controls.ciudadFirma.setValue(contract.datos.ciudadFirma);
        this.contractFG.controls.alquiler.setValue(contract.datos.alquiler);
        this.contractFG.controls.expensas.setValue(contract.datos.expensas);
        this.contractFG.controls.actualizacion.setValue(contract.datos.actualizacion);
        this.contractFG.controls.periodoActualizacion.setValue(contract.datos.periodoActualizacion);
        // Propiedad
        this.direccionFG.setValue({
            tipoCalle: contract.datos.direccionPropiedad.tipoCalle,
            calle: contract.datos.direccionPropiedad.calle,
            numero: contract.datos.direccionPropiedad.numero,
            piso: contract.datos.direccionPropiedad.piso,
            departamento: contract.datos.direccionPropiedad.departamento,
            codigoPostal: contract.datos.direccionPropiedad.codigoPostal,
            localidad: contract.datos.direccionPropiedad.localidad,
        });
        // Propietarios
        this.propietariosArray.clear();
        contract.datos.propietarios.forEach((owner, index) => {
            this.addOwner(owner);
            this.showSection['propietario' + index] = false;
            this.showSection['direccionPropietario' + index] = false;
            this.showSection['representantePropietario' + index] = false;
        });
        // Inquilinos
        this.inquilinosArray.clear();
        contract.datos.inquilinos.forEach((tenant, index) => {
            this.addTenant(tenant);
            this.showSection['inquilino' + index] = false;
            this.showSection['representanteInquilino' + index] = false;
        });
        // Pago
        this.opcionPagoFG.setValue({
            cuotas: contract.datos.opcionPago.cuotas,
            montoPorCuota: contract.datos.opcionPago.montoPorCuota,
            montoTotal: contract.datos.opcionPago.montoTotal,
            tipo: contract.datos.opcionPago?.tipo ?? '',
            codigo: contract.datos.opcionPago?.codigo ?? '',
        });
        // Topes de servicios
        this.contractFG.controls.topeAguaYCloacas.setValue(contract.datos.topeAguaYCloacas ?? 0);
        this.contractFG.controls.topeElectricidad.setValue(contract.datos.topeElectricidad ?? 0);
        this.contractFG.controls.topeGas.setValue(contract.datos.topeGas ?? 0);
        this.contractFG.controls.topeABL.setValue(contract.datos.topeABL ?? 0);
        this.contractFG.controls.expensasOrdinariasTope.setValue(contract.datos.expensasOrdinariasTope ?? 0);
    }

    updateFormGroupFromWarranty(warranty: Warranty) {
        // Propiedad
        this.direccionFG.setValue({
            tipoCalle: warranty.propiedad.direccion.tipoCalle,
            calle: warranty.propiedad.direccion.calle,
            numero: warranty.propiedad.direccion.numero,
            piso: warranty.propiedad.direccion.piso,
            departamento: warranty.propiedad.direccion.departamento,
            codigoPostal: warranty.propiedad.direccion.codigoPostal,
            localidad: warranty.propiedad.direccion.localidad,
        });
        // Propietarios
        this.propietariosArray.clear();
        warranty.propiedad.propietarios.forEach((owner, index) => {
            this.addOwner(owner);
            this.showSection['propietario' + index] = false;
            this.showSection['direccionPropietario' + index] = false;
            this.showSection['representantePropietario' + index] = false;
        });
        // Cotización final
        this.contractFG.controls.alquiler.setValue(warranty.cotizacion?.alquiler);
        this.contractFG.controls.expensas.setValue(warranty.cotizacion?.expensas);
        this.contractFG.controls.actualizacion.setValue(warranty.cotizacion?.actualizacion);
        this.contractFG.controls.periodoActualizacion.setValue(warranty.cotizacion?.periodoActualizacion);
        if (warranty.cotizacion?.tipo === 'COMERCIAL') {
            this.contractFG.controls.actualizacion.enable();
            this.contractFG.controls.actualizacion.setValidators([Validators.required]);
            this.contractFG.controls.periodoActualizacion.enable();
            this.contractFG.controls.periodoActualizacion.setValidators([Validators.required]);
        } else {
            this.contractFG.controls.actualizacion.disable();
            this.contractFG.controls.actualizacion.setValidators([]);
            this.contractFG.controls.periodoActualizacion.disable();
            this.contractFG.controls.periodoActualizacion.setValidators([]);
        }
        this.contractFG.controls.actualizacion.updateValueAndValidity();
        this.contractFG.controls.periodoActualizacion.updateValueAndValidity();
        // Inquilinos
        this.inquilinosArray.clear();
        warranty.inquilinos.forEach((tenant, index) => {
            this.addTenant(tenant);
            this.showSection['inquilino' + index] = false;
            this.showSection['representanteInquilino' + index] = false;
        });
        // Topes de servicios: Valor_C = clamp(alquiler * porcentaje, topeMin, topeMax)
        // Es el tope de boleta cubierta para ese servicio, calculado en el backend al cotizar.
        const servicios = warranty.cotizacion?.servicios ?? [];
        const findValorC = (codigo: string) => Math.round(servicios.find(s => s.codigo === codigo)?.valorC ?? 0);
        this.contractFG.controls.topeAguaYCloacas.setValue(findValorC('AGUA'));
        this.contractFG.controls.topeElectricidad.setValue(findValorC('LUZ'));
        this.contractFG.controls.topeGas.setValue(findValorC('GAS'));
        this.contractFG.controls.topeABL.setValue(findValorC('ABL'));
        this.contractFG.controls.expensasOrdinariasTope.setValue(warranty.cotizacion?.expensas ?? 0);
        // Pago: si ya hay opción pagada, usar esa; si no, usar EFECTIVO de la cotización como default
        if (warranty.pagos?.opcion) {
            this.opcionPagoFG.setValue({
                cuotas: warranty.pagos.opcion.cuotas,
                montoPorCuota: warranty.pagos.opcion.montoPorCuota,
                montoTotal: warranty.pagos.opcion.montoTotal,
                tipo: warranty.pagos.opcion.tipo ?? '',
                codigo: warranty.pagos.opcion.codigo ?? '',
            });
        } else {
            const efectivo = warranty.cotizacion?.opciones?.find(o => o.tipo === 'EFECTIVO' && o.habilitada);
            this.opcionPagoFG.setValue({
                cuotas: efectivo?.cuotas ?? 1,
                montoPorCuota: efectivo?.montoPorCuota ?? warranty.pagos?.principal?.operaciones[0]?.monto ?? 0,
                montoTotal: efectivo?.montoTotal ?? warranty.pagos?.principal?.operaciones[0]?.monto ?? 0,
                tipo: efectivo?.tipo ?? '',
                codigo: efectivo?.codigo ?? '',
            });
        }
    }

    topeMaxConfig(codigo: string): number | null {
        const sc = this.serviciosContratados.find(s => s.codigo === codigo);
        return sc?.maxPorMes ?? null;
    }

    addOwner(owner: Owner) {
        // Generamos el FormGroup
        const ownerFG = new UntypedFormGroup({
            codigo: new UntypedFormControl('', [Validators.required]),
            email: new UntypedFormControl('', [Validators.required, Validators.email]),
            tipoPersona: new UntypedFormControl('FISICA', [Validators.required]),
            personaFisica: generatePhysicalPersonFG('OWNER'),
            personaJuridica: generateLegalPersonFG()
        });
        // Completamos el FG con los datos del owner
        if (owner) {
            this.utilsService.fillFormWithPartialData(ownerFG, owner);
            const tieneRepresentante = owner.tipoPersona === 'FISICA' ? !!owner.personaFisica.representante : !!owner.personaJuridica.representante;
            this.getRepresentativeFG(ownerFG).controls.tieneRepresentante.setValue(tieneRepresentante);
            // Setear tipoDocumento basado en si tiene DNI o Pasaporte
            if (owner.tipoPersona === 'FISICA') {
                const hasDni = owner.personaFisica?.dni && owner.personaFisica.dni.trim() !== '';
                const hasPasaporte = owner.personaFisica?.pasaporte && owner.personaFisica.pasaporte.trim() !== '';

                if (hasPasaporte) {
                    this.getPhysicalPersonFG(ownerFG).controls.tipoDocumento?.setValue('PASAPORTE');
                } else if (hasDni) {
                    this.getPhysicalPersonFG(ownerFG).controls.tipoDocumento?.setValue('DNI');
                }
            }
        }
        // Desactivamos los campos que no se pueden modificar
        this.getPhysicalPersonFG(ownerFG).controls.dni.disable();
        this.getPhysicalPersonFG(ownerFG).controls.sexo.disable();
        this.getLegalPersonFG(ownerFG).controls.cuit.disable();
        this.getRepresentativeFG(ownerFG).controls.tieneRepresentante.disable();
        this.getRepresentativeFG(ownerFG).controls.cargo.disable();
        // Agregamos el FG al array de propietarios
        this.propietariosArray.push(ownerFG);
    }

    addTenant(tenant: Tenant) {
        // Generamos el FormGroup
        const tenantFG = new UntypedFormGroup({
            email: new UntypedFormControl('', []),
            tipoPersona: new UntypedFormControl('FISICA', []),
            personaFisica: generatePhysicalPersonFG('TENANT'),
            personaJuridica: generateLegalPersonFG()
        });
        // Completamos el FG con los datos del tenant
        if (tenant) {
            this.utilsService.fillFormWithPartialData(tenantFG, tenant);
            const tieneRepresentante = tenant.tipoPersona === 'FISICA' ? !!tenant.personaFisica.representante : !!tenant.personaJuridica.representante;
            this.getRepresentativeFG(tenantFG).controls.tieneRepresentante.setValue(tieneRepresentante);
            // Setear tipoDocumento basado en si tiene DNI o Pasaporte
            if (tenant.tipoPersona === 'FISICA') {
                const hasDni = tenant.personaFisica?.dni && tenant.personaFisica.dni.trim() !== '';
                const hasPasaporte = tenant.personaFisica?.pasaporte && tenant.personaFisica.pasaporte.trim() !== '';

                if (hasPasaporte) {
                    this.getPhysicalPersonFG(tenantFG).controls.tipoDocumento?.setValue('PASAPORTE');
                } else if (hasDni) {
                    this.getPhysicalPersonFG(tenantFG).controls.tipoDocumento?.setValue('DNI');
                }
            }
        }
        // Desactivamos los campos que no se pueden modificar
        this.getPhysicalPersonFG(tenantFG).controls.dni.disable();
        this.getPhysicalPersonFG(tenantFG).controls.sexo.disable();
        this.getLegalPersonFG(tenantFG).controls.cuit.disable();
        this.getRepresentativeFG(tenantFG).controls.tieneRepresentante.disable();
        this.getRepresentativeFG(tenantFG).controls.cargo.disable();
        // Agregamos el FG al array de inquilinos
        this.inquilinosArray.push(tenantFG);
    }

    getPersonType(formGroup: UntypedFormGroup) {
        return formGroup.controls.tipoPersona.value as PersonType;
    }

    getPhysicalPersonFG(formGroup: UntypedFormGroup) {
        return formGroup.controls.personaFisica as UntypedFormGroup;
    }

    getLegalPersonFG(formGroup: UntypedFormGroup) {
        return formGroup.controls.personaJuridica as UntypedFormGroup;
    }

    getPersonFG(formGroup: UntypedFormGroup) {
        return this.getPersonType(formGroup) === 'FISICA' ? this.getPhysicalPersonFG(formGroup) : this.getLegalPersonFG(formGroup);
    }

    getAddressFG(formGroup: UntypedFormGroup) {
        return this.getPersonFG(formGroup).controls.direccion as UntypedFormGroup;
    }

    getRepresentativeFG(formGroup: UntypedFormGroup) {
        return this.getPersonFG(formGroup).controls.representante as UntypedFormGroup;
    }

    isValid(control: AbstractControl) {
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            return Object.keys(control.controls).every(key => this.isValid(control.controls[key]));
        } else if (control instanceof UntypedFormControl) {
            return control.status === 'VALID' || control.status === 'DISABLED';
        } else {
            return false;
        }
    }

    isContractValid() {
        let valid = true;
        if (
            !this.isValid(this.contractFG.controls.fechaInicioAlquiler) ||
            !this.isValid(this.contractFG.controls.fechaFinAlquiler) ||
            !this.isValid(this.contractFG.controls.fechaFirma) ||
            !this.isValid(this.contractFG.controls.firmaPresencial) ||
            !this.isValid(this.contractFG.controls.ciudadFirma) ||
            !this.isValid(this.contractFG.controls.direccionPropiedad) ||
            !this.isValid(this.contractFG.controls.alquiler) ||
            !this.isValid(this.contractFG.controls.expensas) ||
            !this.isValid(this.contractFG.controls.actualizacion) ||
            !this.isValid(this.contractFG.controls.periodoActualizacion) ||
            !this.isValid(this.contractFG.controls.opcionPago) ||
            // =============== NUEVAS VALIDACIONES ===============
            !this.isValid(this.contractFG.controls.topeAguaYCloacas) ||
            !this.isValid(this.contractFG.controls.topeElectricidad) ||
            !this.isValid(this.contractFG.controls.topeGas) ||
            !this.isValid(this.contractFG.controls.topeABL) ||
            !this.isValid(this.contractFG.controls.expensasOrdinariasTope) ||
            !this.isValid(this.contractFG.controls.firmante)
        ) {
            valid = false;
            // console.log(1);
        }

        this.propietariosArrayControls.forEach(ownerFG => {
            // console.log('propietarios')
            const physicalPersonFG = this.getPhysicalPersonFG(ownerFG);
            const legalPersonFG = this.getLegalPersonFG(ownerFG);
            const representativeFG = this.getRepresentativeFG(ownerFG);
            const addressFG = this.getAddressFG(ownerFG);
            // Validamos campos sueltos
            if (!this.isValid(ownerFG.controls.codigo)) {

                valid = false;

            }
            if (ownerFG.controls.tipoPersona.value === 'FISICA') {
                // Validamos persona física
                if (
                    !this.isValid(physicalPersonFG.controls.dni) ||
                    !this.isValid(physicalPersonFG.controls.sexo) ||
                    !this.isValid(physicalPersonFG.controls.nombre) ||
                    !this.isValid(physicalPersonFG.controls.apellido) ||
                    !this.isValid(physicalPersonFG.controls.celular) ||
                    !this.isValid(physicalPersonFG.controls.fechaNacimiento)
                ) {
                    valid = false;
                    // console.log(3);

                }
            } else {
                // Validamos persona jurídica
                if (
                    !this.isValid(legalPersonFG.controls.cuit) ||
                    !this.isValid(legalPersonFG.controls.razonSocial) ||
                    !this.isValid(legalPersonFG.controls.tipoSociedad) ||
                    !this.isValid(legalPersonFG.controls.rubro) ||
                    !this.isValid(legalPersonFG.controls.situacionIVA) ||
                    !this.isValid(legalPersonFG.controls.celular)
                ) {
                    valid = false;
                    // console.log(4);

                }
            }

            // Validamos representante
            if (representativeFG.controls.tieneRepresentante.value && !this.isValid(representativeFG)) {
                valid = false;
                // console.log(5);

            }
            // Validamos dirección
            if (!this.isValid(addressFG)) {
                valid = false;
                // console.log(6);

            }


        });

        this.inquilinosArrayControls.forEach(tenantFG => {
            const physicalPersonFG = this.getPhysicalPersonFG(tenantFG);
            const legalPersonFG = this.getLegalPersonFG(tenantFG);
            const representativeFG = this.getRepresentativeFG(tenantFG);
            const addressFG = this.getAddressFG(tenantFG);
            // Validamos campos sueltos
            if (!this.isValid(tenantFG.controls.email)) {
                valid = false;
                // console.log(7);

            }
            if (tenantFG.controls.tipoPersona.value === 'FISICA') {
                // Validamos persona física
                if (
                    !this.isValid(physicalPersonFG.controls.dni) ||
                    !this.isValid(physicalPersonFG.controls.sexo) ||
                    !this.isValid(physicalPersonFG.controls.nombre) ||
                    !this.isValid(physicalPersonFG.controls.apellido) ||
                    !this.isValid(physicalPersonFG.controls.celular) ||
                    !this.isValid(physicalPersonFG.controls.fechaNacimiento)
                ) {
                    valid = false;
                    // console.log(8);

                }
            } else {
                // Validamos persona jurídica
                if (
                    !this.isValid(legalPersonFG.controls.cuit) ||
                    !this.isValid(legalPersonFG.controls.razonSocial) ||
                    !this.isValid(legalPersonFG.controls.tipoSociedad) ||
                    !this.isValid(legalPersonFG.controls.rubro) ||
                    !this.isValid(legalPersonFG.controls.situacionIVA) ||
                    !this.isValid(legalPersonFG.controls.celular) ||
                    !this.isValid(addressFG)
                ) {
                    valid = false;
                    // console.log(9);

                }
            }

            // Validamos representante
            if (representativeFG.controls.tieneRepresentante.value && !this.isValid(representativeFG)) {
                valid = false;
                // console.log(10);

            }
        });

        // console.log(valid);
        return valid;
    }

    generateResultingContract() {
        const contract: any = this.contractFG.getRawValue();
        // tipo/codigo son enums en el backend: enviar '' causa 400, usar null cuando no hay valor
        if (!contract.opcionPago?.tipo) contract.opcionPago.tipo = null;
        if (!contract.opcionPago?.codigo) contract.opcionPago.codigo = null;
        // =============== AGREGAR DATOS DEL FIADOR ===============
        const firmante = contract.firmante;
        if (firmante) {
            contract.fiadorNombre = firmante.nombre;
            contract.fiadorDni = firmante.dni;
            contract.fiadorCargo = firmante.cargo;
            contract.fiadorDomicilio = firmante.domicilio;
            contract.fiadorEmail = firmante.email;
        }
        // Eliminar el objeto firmante completo ya que enviamos los campos individuales
        delete contract.firmante;
        contract.propietarios.forEach(owner => {
            owner.personaFisica = owner.tipoPersona === 'FISICA' ? owner.personaFisica : null;
            owner.personaJuridica = owner.tipoPersona === 'JURIDICA' ? owner.personaJuridica : null;
            const person = owner.tipoPersona === 'FISICA' ? owner.personaFisica : owner.personaJuridica;
            person.representante = person.representante.tieneRepresentante ? person.representante : null;
            if (person.representante)
                delete person.representante.tieneRepresentante;
        });
        contract.inquilinos.forEach(tenant => {
            tenant.personaFisica = tenant.tipoPersona === 'FISICA' ? tenant.personaFisica : null;
            tenant.personaJuridica = tenant.tipoPersona === 'JURIDICA' ? tenant.personaJuridica : null;
            const person = tenant.tipoPersona === 'FISICA' ? tenant.personaFisica : tenant.personaJuridica;
            person.representante = person.representante.tieneRepresentante ? person.representante : null;
            if (person.representante)
                delete person.representante.tieneRepresentante;
        });
        return contract;
    }

    generateContract() {
        this.contractFG.markAllAsTouched();
        if (this.isContractValid()) {
            this.guarantiesService.generateContract(this.data.warranty.id, this.generateResultingContract()).subscribe(warranty => {
                this.cerrarDialogo(warranty);
            }, (error: ErrorResponse) => this.utilsService.alert('Error al generar contrato', error.error?.descripcion));
        }
    }

    openPreview(contractType: ContractType) {
        this.contractFG.markAllAsTouched();
        if (this.isContractValid()) {
            this.guarantiesService.downloadContractPreview(this.data.warranty.id, this.generateResultingContract(), contractType).subscribe(response => {
                const filename = response.headers.get('nombre-archivo');
                this.utilsService.downLoadFile(response.body, filename ?? 'Archivo');
            }, (error: ErrorResponse) => this.utilsService.alert('Error al descargar vista previa', error.error?.descripcion));
        }
    }

}
